import type { Fraction } from '@mimir/common/util/fractionType';


export interface MimirSource {
  /**
   * The ID of the source.
   */
  sourceId: string;

  /**
   * The ID of the Mimir item that this
   * source represents.
   */
  itemId: string;

  /**
   * The starting position of the media source relative the beginning of the timeline.
   */
  startOffsetInSeconds: Fraction;
}


export interface RenderingBox {

  /**
   * A unique ID that identifies this track box.
   */
  boxId: string;

}


export interface RenderingVideoBox extends RenderingBox {

  type: 'video';

  /**
   * The ID of the source this video object refers to,
   * which is guaranteed to be a MimirVideoSource.
   */
  sourceId: string;

  /**
   * The in/out points of this track object relative to the beginning
   * of the source media.
   */
  sourceInPointInSeconds: Fraction;
  sourceOutPointInSeconds: Fraction;
}


export interface RenderingAudioBox extends RenderingBox {

  type: 'audio';

  /**
   * The ID of the source this audio object refers to,
   * which is guaranteed to be either a MimirVideoSource
   * or a MimirAudioSource.
   */
  sourceId: string;

  /**
   * The in/out points of this track object relative to the beginning
   * of the source media.
   */
  sourceInPointInSeconds: Fraction;
  sourceOutPointInSeconds: Fraction;

  /**
   * Which channel of the media is represented by this object.
   */
  channel: number;
}


export interface RenderingImageBox extends RenderingBox {

  type: 'image';

  /**
   * The ID of the source this image object refers to,
   * which is guaranteed to be either a MimirImageSource.
   */
  sourceId: string;

  /**
   * The in/out points of this track object relative to the beginning
   * of the source media.
   */
  sourceInPointInSeconds: Fraction;
  sourceOutPointInSeconds: Fraction;
}


export type RenderingVideoTrackBox = RenderingVideoBox | RenderingImageBox;
export type RenderingAudioTrackBox = RenderingAudioBox;

export type RenderingTrackBox = RenderingVideoTrackBox | RenderingAudioTrackBox;

export type RenderingVideoTrack = RenderingVideoTrackBox[];
export type RenderingAudioTrack = RenderingAudioTrackBox[];


/**
 * This interface describes the public state that the rendering
 * code uses.
 */
export interface TimelineRenderingState {

  /**
   * The Mimir sources used in the timeline, indexed by source IDs.
   * Note that it may happen that several sources have the same item ID
   * and start position, for instance when two consecutive timeline
   * objects are back-to-back cuts from the the same media. In such a
   * case, the rendering code can reuse the same media input.
   */
  sources: Record<string, MimirSource>;

  /**
   * The video tracks to render, where track 0 is the main track,
   * track 1 the first overlay track to be composed on top on track 0
   * and so on.
   */
  videoTracks: RenderingVideoTrack[];


  /**
   * audioTracks[n] corresponds to the output channel n when rendering.
   */
  audioTracks: RenderingAudioTrack[];

}


export function getBlankTimelineRenderingState(): TimelineRenderingState {
  return {
    sources: {},
    videoTracks: [],
    audioTracks: [],
  };
}
